import React, {
  useState,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle,
} from "react";
import Select from "react-select";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageDepartments = forwardRef((props, ref) => {
  const { user } = useContext(AuthContext);
  const [departments, setDepartments] = useState([]);
  const [schools, setSchools] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newDepartment, setNewDepartment] = useState({ name: "", prefix: "", schoolId: "" });
  const [editDepartment, setEditDepartment] = useState({
    id: "",
    name: "",
    prefix: "",
    schoolId: "",
  });
  const [initialEditDepartment, setInitialEditDepartment] = useState({
    id: "",
    name: "",
    prefix: "",
    schoolId: "",
  });
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const columns = [
    { key: "school", label: "School", sortable: true },
    { key: "name", label: "Department Name", sortable: true },
    { key: "prefix", label: "Prefix", sortable: true },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  useImperativeHandle(ref, () => ({
    openModal: () => setIsModalOpen(true),
  }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch departments
        const departmentsResponse = await fetchWithAuth(
          `${serverUrl}/api/department/get-all/${user.instituteId}`
        );
        if (!departmentsResponse) return;
        if (!departmentsResponse.ok) throw new Error("Failed to fetch departments");
        const departmentsData = await departmentsResponse.json();
        setDepartments(departmentsData);

        // Fetch schools
        try {
          const schoolsResponse = await fetchWithAuth(
            `${serverUrl}/api/school/get-all/${user.instituteId}`
          );
          if (schoolsResponse && schoolsResponse.ok) {
            const schoolsData = await schoolsResponse.json();
            setSchools(schoolsData);
          }
        } catch (schoolErr) {
          // Schools are optional, so we don't show error if they don't exist
          console.log("Schools not available:", schoolErr.message);
        }

        setError(null);
      } catch (err) {
        setError(err.message);
        swalError("Failed to load departments.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.instituteId]);

  const handleDelete = async (departmentId) => {
    const result = await swalConfirm(
      "Are you sure?",
      "You won't be able to revert this!",
      "Yes, delete it!",
      "Cancel"
    );

    if (result.isConfirmed) {
      setIsDeleting(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/department/delete/${departmentId}`,
          {
            method: "DELETE",
          }
        );
        if (!response) return;
        if (!response.ok) {
          const errorData = await response.json();  
          throw new Error(errorData.message || "Failed to delete department");
        }
        setDepartments(departments.filter((dept) => dept._id !== departmentId));
        swalSuccess("Department deleted successfully.");
      } catch (err) {
        swalError(err.message || "Failed to delete department.");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    if (!newDepartment.name.trim() || !newDepartment.prefix.trim()) {
      swalError("Please provide the department name and prefix.");
      return;
    }

    setIsCreating(true);
    try {
      const requestBody = {
        name: newDepartment.name.trim(),
        prefix: newDepartment.prefix.trim().toUpperCase(),
        instituteId: user.instituteId,
      };

      // Add schoolId if selected
      if (newDepartment.schoolId) {
        requestBody.schoolId = newDepartment.schoolId;
      }

      const response = await fetchWithAuth(`${serverUrl}/api/department/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create department");
      }
      const createdDepartment = await response.json();
      setDepartments([...departments, createdDepartment]);
      setIsModalOpen(false);
      setNewDepartment({ name: "", prefix: "", schoolId: "" });
      swalSuccess("Department created successfully.");
    } catch (err) {
      swalError("Failed to create department.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditDepartment = async (e) => {
    e.preventDefault();
    if (!editDepartment.name.trim() || !editDepartment.prefix.trim()) {
      swalError("Please provide the department name and prefix.");
      return;
    }

    setIsUpdating(true);
    try {
      const requestBody = {
        name: editDepartment.name.trim(),
        prefix: editDepartment.prefix.trim().toUpperCase(),
        instituteId: user.instituteId,
      };

      // Add schoolId if selected
      if (editDepartment.schoolId) {
        requestBody.schoolId = editDepartment.schoolId;
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/department/update/${editDepartment.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update department");
      }
      const updatedDepartment = await response.json();
      setDepartments(
        departments.map((dept) =>
          dept._id === updatedDepartment._id ? updatedDepartment : dept
        )
      );
      setIsEditModalOpen(false);
      setEditDepartment({ id: "", name: "", prefix: "", schoolId: "" });
      setInitialEditDepartment({ id: "", name: "", prefix: "", schoolId: "" });
      setIsEditChanged(false);
      swalSuccess("Department updated successfully.");
    } catch (err) {
      swalError("Failed to update department.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    if (name === "prefix") {
      updatedValue = value.toUpperCase().slice(0, 4);
    }
    const updatedDepartment = { ...editDepartment, [name]: updatedValue };
    setEditDepartment(updatedDepartment);
    setIsEditChanged(
      updatedDepartment.name !== initialEditDepartment.name ||
      updatedDepartment.prefix !== initialEditDepartment.prefix ||
      updatedDepartment.schoolId !== initialEditDepartment.schoolId
    );
  };

  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    if (name === "prefix") {
      updatedValue = value.toUpperCase().slice(0, 4);
    }
    setNewDepartment({ ...newDepartment, [name]: updatedValue });
  };

  // Create school options for Select component
  const schoolOptions = schools.map((school) => ({
    value: school._id || school.id,
    label: school.name,
  }));

  // Helper function to get school name by ID
  const getSchoolName = (schoolId) => {
    if (!schoolId) return "-";
    const school = schools.find((s) => (s._id || s.id) === schoolId);
    return school ? school.name : "Unknown";
  };

  const enhancedDepartments = departments.map((dept) => ({
    ...dept,
    school: getSchoolName(dept.schoolId),
    edit: (
      <button
        className="edit-btn"
        onClick={() => {
          setEditDepartment({
            id: dept._id,
            name: dept.name,
            prefix: dept.prefix,
            schoolId: dept.schoolId || "",
          });
          setInitialEditDepartment({
            id: dept._id,
            name: dept.name,
            prefix: dept.prefix,
            schoolId: dept.schoolId || "",
          });
          setIsEditModalOpen(true);
          setIsEditChanged(false);
        }}
        disabled={isDeleting || isUpdating || isCreating}
      >
        <i className="fa-solid fa-pen-to-square"></i>
      </button>
    ),
    delete: (
      <button
        className="delete-btn"
        onClick={() => handleDelete(dept._id)}
        disabled={isDeleting || isUpdating || isCreating}
      >
        <i className="fa-solid fa-trash-can"></i>
      </button>
    ),
  }));

  return (
    <>
      <div className="header">
        <button className="create-btn" onClick={() => setIsModalOpen(true)}>
          Add Department +
        </button>
      </div>
      <div className="manage-container">
        {loading && <Loader fontSize="2rem" />}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && (
          <Table
            data={enhancedDepartments}
            columns={columns}
            caption="Departments"
          />
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => !isCreating && setIsModalOpen(false)}
      >
        <h2>Add New Department</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Fill in the details below to add a new department to your institute.
        </p>
        <form onSubmit={handleCreateDepartment} className="department-form">
          {schools.length > 0 ? (
            <div className="form-group">
              <label htmlFor="schoolId">
                <i className="fa-solid fa-asterisk requiredIcon"></i> School
              </label>
              <Select
                id="schoolId"
                name="schoolId"
                options={schoolOptions}
                value={
                  newDepartment.schoolId
                    ? schoolOptions.find(
                      (option) => option.value === newDepartment.schoolId
                    ) || null
                    : null
                }
                onChange={(selectedOption) => {
                  setNewDepartment({
                    ...newDepartment,
                    schoolId: selectedOption ? selectedOption.value : "",
                  });
                }}
                isClearable
                placeholder="Select or search school..."
                isDisabled={isCreating}
                required
              />
              <small>You can search for a school by name.</small>
            </div>
          ) : null}

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Department
              Name
            </label>
            <input
              type="text"
              name="name"
              value={newDepartment.name}
              placeholder="Enter the department name"
              onChange={handleCreateInputChange}
              required
              disabled={isCreating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Department
              Prefix
            </label>
            <input
              type="text"
              name="prefix"
              value={newDepartment.prefix}
              onChange={handleCreateInputChange}
              placeholder="e.g. CSE, ECE"
              maxLength={4}
              required
              disabled={isCreating}
            />
          </div>
          {isCreating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Create Department
            </button>
          )}
        </form>
      </Modal>
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isUpdating && setIsEditModalOpen(false)}
      >
        <h2>Edit Department</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Update the details below to edit the department information.
        </p>
        <form onSubmit={handleEditDepartment} className="department-form">
          {schools.length > 0 ? (
            <div className="form-group">
              <label htmlFor="editSchoolId">
                School
              </label>
              <Select
                id="editSchoolId"
                name="schoolId"
                options={schoolOptions}
                value={
                  editDepartment.schoolId
                    ? schoolOptions.find(
                      (option) => option.value === editDepartment.schoolId
                    ) || null
                    : null
                }
                onChange={(selectedOption) => {
                  const updatedDepartment = {
                    ...editDepartment,
                    schoolId: selectedOption ? selectedOption.value : "",
                  };
                  setEditDepartment(updatedDepartment);
                  setIsEditChanged(
                    updatedDepartment.name !== initialEditDepartment.name ||
                    updatedDepartment.prefix !== initialEditDepartment.prefix ||
                    updatedDepartment.schoolId !== initialEditDepartment.schoolId
                  );
                }}
                isClearable
                placeholder="Select or search school..."
                isDisabled={isUpdating}
              />
              <small>You can search for a school by name.</small>
            </div>
          ) : null}

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Department
              Name
            </label>
            <input
              type="text"
              name="name"
              value={editDepartment.name}
              placeholder="Enter the department name"
              onChange={handleEditInputChange}
              required
              disabled={isUpdating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Department
              Prefix
            </label>
            <input
              type="text"
              name="prefix"
              value={editDepartment.prefix}
              onChange={handleEditInputChange}
              placeholder="e.g. CSE, ECE"
              maxLength={4}
              required
              disabled={isUpdating}
            />
          </div>
          {isUpdating ? (
            <Loader />
          ) : (
            <button
              type="submit"
              className="btn-submit"
              disabled={!isEditChanged || isUpdating}
              onClick={(e) => {
                if (!isEditChanged && !isUpdating) {
                  e.preventDefault();
                  swalWarning("No changes detected.");
                }
              }}
            >
              Update Department
            </button>
          )}
        </form>
      </Modal>
    </>
  );
});

export default ManageDepartments;
